<script setup lang="ts">
import { computed, shallowRef, watch } from 'vue';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import { store as vuex } from '@/store/store';
import { useMarketplaceApiService } from '@/modules/marketplace/services/marketplace-api.service';

const marketplaceApiService = useMarketplaceApiService();

const props = withDefaults(defineProps<{ orderRef: string; icon?: boolean }>(), { icon: false });

const emit = defineEmits<{
  (e: 'action', payload: { error?: boolean; orderRef: string; message: string });
}>();

const hasTraderUserRole = computed(() => vuex.getters.hasTraderUserRole);
const socketEvents = computed(() => vuex.state.socketEvents);

watch(
  () => socketEvents.value.marketplace.orders,
  (payload) => {
    if (payload?.orderRef === props.orderRef) {
      isLoading.value = false;
    }
  }
);

const isLoading = shallowRef(false);

async function cancelOrder(): Promise<void> {
  isLoading.value = true;

  try {
    await marketplaceApiService.cancelOrder(props.orderRef);

    emit('action', {
      orderRef: props.orderRef,
      message: 'Order has been successfully canceled',
    });

    // Note: we leave things in loading state and wait for a socket event to unset that
  } catch (err) {
    const errorMessage = i18nServerMessage(err);
    emit('action', {
      error: true,
      orderRef: props.orderRef,
      message: `Operation failed: ${errorMessage}`,
    });
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <ConfirmDialogWrapper
    :options="{
      message: 'You are about to cancel the order.',
      title: 'Order cancellation',
      rejectText: 'Keep order',
      acceptText: 'Cancel order',
    }"
    v-on="$listeners"
  >
    <template #default="{ confirm }">
      <!-- icon button variant -->
      <AuroraBtn
        v-if="icon"
        class="icon-action"
        data-test="cancel-order-icon"
        :disabled="isLoading || !hasTraderUserRole"
        icon
        :loading="isLoading"
        timeframe="createLoans"
        x-small
        @click="confirm(() => cancelOrder())"
      >
        <v-icon size="24">mdi-close-circle</v-icon>
      </AuroraBtn>

      <!-- regular button variant -->
      <AuroraBtn
        v-else
        color="red"
        data-test="cancel-order-btn"
        :disabled="isLoading || !hasTraderUserRole"
        :loading="isLoading"
        timeframe="createLoans"
        @click="confirm(() => cancelOrder())"
      >
        Cancel
      </AuroraBtn>
    </template>
  </ConfirmDialogWrapper>
</template>
